import { useState, useRef } from 'react';
import { Button } from './button';
import { Textarea } from './textarea';
import { Label } from './label';
import { X, Upload, Image as ImageIcon, Loader2, Link as LinkIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getImageUrl } from '../../lib/utils';

export function ImageUpload({
  value,
  onChange,
  folder = 'general',
  imageType = null,
  multiple = false,
  maxFiles = 5,
  label = 'Upload Image',
  description = 'Upload an image file (max 4MB until server restart)',
  className = '',
  accept = 'image/*'
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [failedImageUrls, setFailedImageUrls] = useState({});
  const fileInputRef = useRef(null);
  const MAX_SIZE_MB = 10;
  const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024;

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    const fileList = Array.from(files);

    // Validate file count
    if (multiple) {
      const currentCount = Array.isArray(value) ? value.length : 0;
      if (currentCount + fileList.length > maxFiles) {
        toast.error(`Maximum ${maxFiles} files allowed`);
        return;
      }
    } else if (fileList.length > 1) {
      toast.error('Only one file allowed');
      return;
    }

    // Validate file types and size

    for (const file of fileList) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return;
      }

      if (file.size > MAX_SIZE) {
        toast.error(`${file.name} is too large (max ${MAX_SIZE_MB}MB)`);
        return;
      }
    }

    setUploading(true);
    try {
      if (multiple && fileList.length > 1) {
        // Upload multiple files
        const formData = new FormData();
        fileList.forEach(file => formData.append('files', file));
        formData.append('folder', folder);
        if (imageType) formData.append('image_type', imageType);

        const response = await api.post('/upload/multiple', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const newUrls = response.data.files.map(f => f.url);
        const currentUrls = Array.isArray(value) ? value : [];
        onChange([...currentUrls, ...newUrls]);

        toast.success(`${newUrls.length} images uploaded and optimized successfully`);
      } else {
        // Upload single file
        const formData = new FormData();
        formData.append('file', fileList[0]);
        formData.append('folder', folder);
        if (imageType) formData.append('image_type', imageType);

      

        const response = await api.post('/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });


        if (multiple) {
          const currentUrls = Array.isArray(value) ? value : [];
          onChange([...currentUrls, response.data.url]);
        } else {
          onChange(response.data.url);
        }

        toast.success('Image uploaded and optimized successfully');
      }
    } catch (error) {
      console.error('Upload error:', error);
      
      // Handle specific error types
      if (error.response?.status === 413) {
        toast.error(`Upload rejected (413). File exceeds server limit (max ${MAX_SIZE_MB}MB).`);
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Invalid file format');
      } else {
        toast.error('Failed to upload image. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (urlToRemove) => {
    // Just remove from local state - do not delete from server immediately
    // The orphan file will remain in S3 but the reference will be removed from the product on save
    if (multiple) {
      const currentUrls = Array.isArray(value) ? value : [];
      onChange(currentUrls.filter(url => url !== urlToRemove));
    } else {
      onChange('');
    }
  };

  const handleMove = (index, direction) => {
    if (!multiple || !Array.isArray(value)) return;

    const newUrls = [...value];
    const targetIndex = direction === 'left' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newUrls.length) {
      [newUrls[index], newUrls[targetIndex]] = [newUrls[targetIndex], newUrls[index]];
      onChange(newUrls);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const currentImages = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);
  const canUploadMore = multiple ? currentImages.length < maxFiles : currentImages.length === 0;

  return (
    <div className={`space-y-4 ${className}`}>
      {label && <Label>{label}</Label>}

      {/* Upload Area */}
      {canUploadMore && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${dragOver
            ? 'border-primary bg-primary/5'
            : 'border-slate-300 hover:border-primary hover:bg-slate-50'
            }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />

          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-8 h-8 text-muted-foreground" />
              <p className="text-sm font-medium">Click to upload or drag and drop</p>
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
          )}
        </div>
      )}

      {/* Image Preview */}
      {currentImages.length > 0 && (
        <div className={`grid gap-4 ${multiple ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-1'}`}>
          {currentImages.map((imageUrl, index) => (
            <div key={`${imageUrl}-${index}`} className="relative group">
              <div className="aspect-square rounded-lg overflow-hidden bg-slate-100 relative">
                {failedImageUrls[imageUrl] && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-slate-100 text-slate-500 px-3 text-center">
                    <ImageIcon className="w-8 h-8" />
                    <p className="text-xs font-medium">Image unavailable</p>
                    <p className="text-[10px]">
                      {typeof navigator !== 'undefined' && navigator.onLine ? 'Failed to load from source URL' : 'You appear to be offline'}
                    </p>
                  </div>
                )}
                <img
                  src={getImageUrl(imageUrl)}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-full object-contain bg-white"
                  onError={(e) => {
                    console.error('Image load error:', imageUrl);
                    setFailedImageUrls((prev) => ({ ...prev, [imageUrl]: true }));
                    e.currentTarget.style.opacity = '0';
                  }}
                  onLoad={() => {
                    setFailedImageUrls((prev) => {
                      if (!prev[imageUrl]) return prev;
                      const next = { ...prev };
                      delete next[imageUrl];
                      return next;
                    });
                  }}
                />

                {/* Reorder Buttons */}
                {multiple && (
                  <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {index > 0 && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon"
                        className="w-6 h-6 h-6 bg-white/80 hover:bg-white"
                        onClick={(e) => { e.stopPropagation(); handleMove(index, 'left'); }}
                      >
                        <ChevronLeft className="w-3 h-3 text-slate-700" />
                      </Button>
                    )}
                    {index < currentImages.length - 1 && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon"
                        className="w-6 h-6 bg-white/80 hover:bg-white"
                        onClick={(e) => { e.stopPropagation(); handleMove(index, 'right'); }}
                      >
                        <ChevronRight className="w-3 h-3 text-slate-700" />
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => handleRemove(imageUrl)}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )
      }

      {/* Upload More Button (for multiple) */}
      {
        multiple && currentImages.length > 0 && canUploadMore && (
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload More Images ({currentImages.length}/{maxFiles})
          </Button>
        )
      }

      {/* Manual URL Input */}
      {
        canUploadMore && (
          <div className="pt-2 border-t space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Add via URL</Label>
            </div>

            <div className="flex gap-2">
              <div className="flex-1 space-y-2">
                {/* We will use a simple textarea that accepts one URL per line for better UX than 5 separate inputs */}
                <Textarea
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder={`Paste image URLs here (one per line)\nYou can add up to ${maxFiles - currentImages.length} more images`}
                  className="min-h-[80px] text-sm font-mono"
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                className="h-auto self-stretch"
                onClick={() => {
                  if (!urlInput.trim()) return;
                  const urls = urlInput.split(/[\n,]+/).map(u => u.trim()).filter(Boolean);

                  if (urls.length === 0) return;

                  const validUrls = [];
                  for (const url of urls) {
                    try {
                      new URL(url);
                      validUrls.push(url);
                    } catch (_) {
                      // Skip invalid
                    }
                  }

                  if (validUrls.length === 0) {
                    toast.error('No valid URLs found');
                    return;
                  }

                  if (multiple) {
                    const currentCount = Array.isArray(value) ? value.length : 0;
                    if (currentCount + validUrls.length > maxFiles) {
                      toast.error(`Cannot add ${validUrls.length} images. Max ${maxFiles} allowed.`);
                      return;
                    }
                    onChange(Array.isArray(value) ? [...value, ...validUrls] : validUrls);
                  } else {
                    onChange(validUrls[0]);
                  }

                  setUrlInput('');
                  toast.success(`Added ${validUrls.length} image(s)`);
                }}
                disabled={!urlInput.trim()}
              >
                <LinkIcon className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground text-right space-x-2">
              <span>Supports multiple URLs (one per line)</span>
            </p>
          </div>
        )
      }
    </div >
  );
}

// Single Image Upload Component
export function SingleImageUpload(props) {
  return <ImageUpload {...props} multiple={false} />;
}

// Multiple Image Upload Component
export function MultipleImageUpload(props) {
  return <ImageUpload {...props} multiple={true} />;
}
